/**
 * ONE message-detail renderer — the metadata header, the governed participant list (F.2) and the
 * thread-aware body (F.4) — shared by the full page (`?sel=msg:N`) and the modal (`?msg=N`, F.3).
 *
 * It exists because the modal must show exactly what the page shows, including the awkward states:
 * a SEALED subject (null — either body_gated because the caller is not on the thread, or genuinely
 * empty; the payload carries no distinction by design), a 403 body ("not on this thread"), and a 404
 * body (entitled, but nothing stored). Those three were the reason not to write a second, simpler
 * renderer for the overlay: a "simpler" one is a renderer that quietly drops the sealed cases.
 *
 * Every fetch here is on-demand per message id — nothing is preloaded for a list, and the body read is
 * re-gated and audited server-side on every call (S5), which is why the modal fetches on OPEN.
 */
import { useMemo } from 'react';
import { ApiError } from '../../api/client';
import {
  crmEmailContentBase, useCrmEmailBody, useCrmEmailContent, useCrmContentOpenKey, useCrmEmailBodyKey, useCrmEmailContentKey,
  useCrmEmailMeta,
} from '../../hooks/useCrm';
import { useCrmContentOnDemand } from '../../hooks/useCrmContentOnDemand';
import { EmailAttachments } from '../shared/EmailAttachments';
import { attachmentListStateFromQuery } from '../shared/attachmentDisplay';
import { InlineMessageBody } from '../shared/InlineMessageBody';
import { inlineMessageStateWithContent } from '../shared/inlineMessageState';
import { MailParticipants, type OpenPerson } from './CrmParticipants';
import { fmtShortDate } from './crmFormat';
import styles from './crm.module.css';

/** F.4's threaded renderer MOVED to `components/shared/ThreadedBody.tsx` (timeline-inline spec §6)
 *  so the page, the modal and the timeline row cannot drift. Re-exported from its old path, which
 *  is where every caller and every doc reference still points. */
export { ThreadedBody } from '../shared/ThreadedBody';

/** 'email_sent' | 'email_received' → the header's direction word. Null for anything else so an
 *  unexpected tag drops out of the meta line rather than printing a raw key. */
function directionLabel(activityType?: string | null): string | null {
  if (activityType === 'email_sent') return 'Sent';
  if (activityType === 'email_received') return 'Received';
  return null;
}

/** The body card: entitlement states + the threaded renderer.
 *
 * The rendering now goes through the SHARED `InlineMessageBody` (timeline-inline spec §6) — the same
 * component the entity timeline's expanded row uses — so the modal, the page and the row cannot
 * drift into three stories about one message. The card chrome (title + "entitlement-gated per read")
 * stays here, because it is the page's furniture, not the body's.
 *
 * `maxHeight="none"`: this card already sits inside the modal's / page's own scroll container, and a
 * second nested scrollbar is worse than a long page. The 75vh cap is the timeline ROW's need.
 */
function BodyCard({ emailId }: { emailId: number }) {
  const body = useCrmEmailBody(emailId, true);
  // EC-2: this surface's OWN governed status read, through its own audited door. The SAME shared
  // renderer the timeline row uses, so the page, the modal and the row cannot drift into three
  // stories about one message's documents — or three answers about one blocked file.
  const content = useCrmEmailContent(emailId, true);
  // ⛔ ONE ladder, and a refetch counts as LOADING. Passing the raw query's `data` down painted
  // the previous open's filenames with a live Download button after a revocation, because React
  // Query keeps the last successful data across a refetch and across one that fails (BLOCKER 1).
  // 403 / 404 render nothing: the body card above already tells the entitlement story, and a
  // flag-off 404 must not read as a defect (the CrmLinkedRecords rule).
  const statusKey = useCrmEmailContentKey(emailId);
  const openKey = useCrmContentOpenKey(`email:${emailId}`);
  // Memoised: a fresh array every render would re-run the loop's effects on every render.
  const bodyKey = useCrmEmailBodyKey(emailId);
  const bodyInvalidateKeys = useMemo(() => [bodyKey], [bodyKey]);
  const contentState = attachmentListStateFromQuery(content);
  // EC-5 — this surface's own acquisition loop. `enabled` is true because this card mounts on an
  // explicit open; a collapsed row never reaches here, so no POST can be fired by rendering a list.
  const onDemand = useCrmContentOnDemand({
    base: crmEmailContentBase(emailId),
    openKey: openKey,
    content: contentState === 'ready' ? content.data : undefined,
    statusKey: statusKey,
    enabled: true,
    // ⛔ **THE BODY QUERY IS RE-READ WHEN AN ACQUISITION LANDS** (Release B canary, msg 494083).
    // The status door and the body door are two different queries: without this the POST settles,
    // the envelope flips to `ready`, and the BODY query keeps serving the 404 it cached before the
    // fetch — so the surface still reads "No stored body for this message (metadata only)" for a
    // body that is sitting in the store. The key comes from the hook, never hand-built, so it
    // cannot drift from the one `useCrmEmailBody` actually reads.
    extraInvalidateKeys: bodyInvalidateKeys,
  });
  // ONE ladder, shared with the timeline row (review F3) — including the `isError` branch a copied
  // one loses, which is what kept a status-less failure on "Checking entitlement…" forever. EC-5
  // widens it with what the envelope knows: a 404 on the byte door reads as "Retrieving…" while an
  // acquisition for this body is in flight, and as "No longer available" once one has completed.
  const state = inlineMessageStateWithContent(
    body, contentState === 'ready' ? content.data?.body : undefined,
    { pending: onDemand.pending.has('body') });
  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        Body
        <span className={styles.cardTitleNote}>entitlement-gated per read</span>
      </div>
      <InlineMessageBody state={state} text={body.data} maxHeight="none" />
      <EmailAttachments
        emailId={emailId}
        state={contentState}
        content={contentState === 'ready' ? content.data : undefined}
        contentBase={crmEmailContentBase(emailId)}
        onDemand={onDemand}
      />
    </div>
  );
}

interface Props {
  emailId: number;
  /** In-place profile navigation for a linked participant. Omitted → names render without links. */
  openPerson?: OpenPerson;
}

export function CrmMessageDetail({ emailId, openPerson }: Props) {
  const meta = useCrmEmailMeta(emailId);
  return (
    <>
      {meta.isLoading && <div className={styles.loading}>Loading message…</div>}
      {meta.error != null && (
        <div className={styles.error}>
          {meta.error instanceof ApiError && meta.error.status === 404
            ? "This message doesn't exist or isn't visible to you."
            : 'Failed to load message.'}
        </div>
      )}
      {meta.data && (
        <>
          <div className={styles.card}>
            <h2 className={styles.entityName}>
              <span className={`${styles.chip} ${styles.kindEmail}`}>Email</span>
              <span className={meta.data.subject == null ? styles.timelineSealed : undefined}>
                {meta.data.subject ?? '(no subject)'}
              </span>
            </h2>
            {/* Direction from EMBER's perspective, off the SAME `activity_type` tag the timeline chip
                reads — so the header can no longer say "inbound" on a message the timeline calls sent.
                The raw column arrives as `provenance_direction` (which mailbox folder the first
                sighting came from) and is deliberately not rendered. */}
            <div className={styles.entityMeta}>
              {[fmtShortDate(meta.data.sent_at), directionLabel(meta.data.activity_type)]
                .filter(Boolean)
                .join(' · ')}
            </div>
            <MailParticipants emailId={emailId} openPerson={openPerson} flush />
          </div>
          {/* ⚠ KEYED BY emailId. `CrmMessageDetail` stays mounted when the modal's `msg` param moves
              from one message to another, so without this the body card's "show earlier messages"
              toggle and per-segment expansion state carried over — the next message opened
              pre-expanded at whatever indexes the previous reader happened to click (review minor 10). */}
          <BodyCard key={emailId} emailId={emailId} />
        </>
      )}
    </>
  );
}
